/**
 * Submission Pack page shell (Specs/submission-pack-spec.md).
 *
 * The page is the only caller of `useSubmissionPackStore.load` on this route —
 * children read the loaded pack from the store and never re-fetch it. (The
 * Overview tile `PackSnapshot` loads it too, but on its own route: the two
 * never mount together.) Team members are
 * fetched once here too (the SubmissionChecklistExtraction precedent), so the
 * rows share one roster instead of one request each.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, ClipboardList, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { NEEDS_FROM_YOU_ANCHOR, NeedsFromYou } from '@/components/pack/NeedsFromYou'
import { PACK_BLOCKERS_ANCHOR, PackBlockers } from '@/components/pack/PackBlockers'
import { PackFilterBar } from '@/components/pack/PackFilterBar'
import { PackHeader } from '@/components/pack/PackHeader'
import { PackLinkCell } from '@/components/pack/PackLinkCell'
import { PackList } from '@/components/pack/PackList'
import { SubmissionItemDrawer } from '@/components/pack/SubmissionItemDrawer'
import {
  api,
  type BidTodo,
  type NeedsFromYouEntry,
  type PackItem,
  type SubmissionPackBlockers,
  type SubmissionPackRollup,
  type SubmissionStatus,
  type TriageClass,
} from '@/lib/api'
import {
  DEFAULT_PACK_FILTERS,
  filterPackItems,
  isPackFilterActive,
  matchesPackFilters,
  shouldFoldDone,
  type PackFilters,
} from '@/lib/pack-filters'
import { getTodoTargetPath } from '@/lib/response-linking'
import { validateFile } from '@/lib/schemas'
import { useAuthStore } from '@/stores/auth-store'
import { usePreviewStore } from '@/stores/preview-store'
import { useResponsesStore } from '@/stores/responses-store'
import { useSubmissionPackStore } from '@/stores/submission-pack-store'
import { useUnifiedAiPanelStore } from '@/stores/unified-ai-panel-store'

/** Stands in only for the beat between mount and the first pack read. */
const EMPTY_ROLLUP: SubmissionPackRollup = {
  ready: 0,
  inProgress: 0,
  notReady: 0,
  notRequired: 0,
  total: 0,
}

/** Same beat, for the export dialog's confirm step: nothing counted yet. */
const EMPTY_BLOCKERS: SubmissionPackBlockers = {
  mandatoryAtRisk: 0,
  missingHandovers: 0,
  undraftedResponses: 0,
  unacknowledged: 0,
  overWordLimit: 0,
  openTodos: 0,
  removedByAddendum: 0,
}

interface SubmissionPackPageProps {
  bidId: string
  readOnly?: boolean
}

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

export function SubmissionPackPage({ bidId, readOnly = false }: SubmissionPackPageProps) {
  const pack = useSubmissionPackStore((s) => s.pack)
  const isLoading = useSubmissionPackStore((s) => s.isLoading)
  const error = useSubmissionPackStore((s) => s.error)
  const load = useSubmissionPackStore((s) => s.load)
  const setStatus = useSubmissionPackStore((s) => s.setStatus)
  const setOwner = useSubmissionPackStore((s) => s.setOwner)
  const setAcknowledged = useSubmissionPackStore((s) => s.setAcknowledged)
  const addItem = useSubmissionPackStore((s) => s.addItem)
  const removeItem = useSubmissionPackStore((s) => s.removeItem)
  const linkItem = useSubmissionPackStore((s) => s.linkItem)
  const unlinkItem = useSubmissionPackStore((s) => s.unlinkItem)
  const uploadAndLinkItem = useSubmissionPackStore((s) => s.uploadAndLinkItem)
  const ensureOverlay = useSubmissionPackStore((s) => s.ensureOverlay)
  const answerAsk = useSubmissionPackStore((s) => s.answerAsk)
  const resolveAsk = useSubmissionPackStore((s) => s.resolveAsk)
  const setAskOwner = useSubmissionPackStore((s) => s.setAskOwner)
  const addAsk = useSubmissionPackStore((s) => s.addAsk)
  const createArtifact = useResponsesStore((s) => s.createArtifact)
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  const navigate = useNavigate()

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  // An empty pack shows the empty state, not four blank groups. This flips to
  // the real list, which then opens its add dialog straight away.
  const [showEmptyList, setShowEmptyList] = useState(false)
  const [activeItemId, setActiveItemId] = useState<string | null>(null)
  // A row to scroll to once the render that sets it has committed. An effect,
  // not requestAnimationFrame: a row being unfolded or un-filtered only exists
  // after PackList commits, and passive effects run after that commit.
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null)
  // The list's filters. The blocker chips and the `?item=` deep link set and
  // clear them too, which is why they live here and not in the filter bar.
  const [filters, setFilters] = useState<PackFilters>(DEFAULT_PACK_FILTERS)
  // "Waiting on the team" starts collapsed; its blockers chip opens it.
  const [isNeedsExpanded, setIsNeedsExpanded] = useState(false)
  const [scrollToNeeds, setScrollToNeeds] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    void load(bidId)
  }, [bidId, load])

  // Document previews opened from a link chip belong to this page (D-12). Close
  // them on unmount so a preview doesn't outlive the tab that opened it — but
  // only ours: a chat preview open beside the page is not this page's to close.
  useEffect(
    () => () => {
      const preview = usePreviewStore.getState()
      if (preview.context === 'pack') preview.closePreview()
    },
    []
  )

  useEffect(() => {
    void api
      .get<{ members: TeamMember[] }>(`/bids/${bidId}/members`)
      .then((data) => setTeamMembers(data.members))
      .catch(() => {
        /* non-critical — the picker just shows nobody to assign */
      })
  }, [bidId])

  // Every mutation re-reads the pack, so a rejection leaves the last good pack
  // on screen. What it must not do is fail silently — the row would just look
  // like it ignored the click.
  const report = useCallback((message: string, error?: unknown) => {
    // Arity matters: passing an absent `error` through would log a trailing
    // `undefined` on the validation paths that only carry a message.
    if (error === undefined) console.error(`[SubmissionPack] ${message}`)
    else console.error(`[SubmissionPack] ${message}`, error)
    toast.error(message)
  }, [])

  const onFailure = useCallback(
    (message: string) => (error: unknown) => report(message, error),
    [report]
  )

  const handleSetStatus = useCallback(
    (itemId: string, status: SubmissionStatus | null) => {
      void setStatus(itemId, status).catch(onFailure('Could not update the status'))
    },
    [setStatus, onFailure]
  )
  const handleSetOwner = useCallback(
    (itemId: string, ownerId: string | null) => {
      void setOwner(itemId, ownerId).catch(onFailure('Could not update the owner'))
    },
    [setOwner, onFailure]
  )
  const handleSetAcknowledged = useCallback(
    (itemId: string, acknowledge: boolean) => {
      void setAcknowledged(itemId, acknowledge).catch(
        onFailure('Could not update the acknowledgement')
      )
    },
    [setAcknowledged, onFailure]
  )
  const handleAddItem = useCallback(
    (title: string, triageClass: TriageClass, description: string | null) => {
      void addItem({ title, triageClass, description }).catch(
        onFailure('Could not add the item')
      )
    },
    [addItem, onFailure]
  )
  const handleRemoveItem = useCallback(
    (itemId: string) => {
      void removeItem(itemId).catch(onFailure('Could not remove the item'))
    },
    [removeItem, onFailure]
  )

  // The store's PackLinkTarget is a discriminated union, so one of these two
  // calls always nulls the other link column — a body carrying both a
  // linkedArtifactId and a linkedDocumentId can never be built here.
  const handleLinkArtifact = useCallback(
    (itemId: string, artifactId: string, sectionKey: string | null) =>
      linkItem(itemId, { kind: 'artifact', artifactId, sectionKey }).catch(
        onFailure('Could not link the response')
      ),
    [linkItem, onFailure]
  )
  const handleLinkDocument = useCallback(
    (itemId: string, documentId: string) =>
      linkItem(itemId, { kind: 'document', documentId }).catch(
        onFailure('Could not link the document')
      ),
    [linkItem, onFailure]
  )
  const handleUnlink = useCallback(
    (itemId: string) => unlinkItem(itemId).catch(onFailure('Could not remove the link')),
    [unlinkItem, onFailure]
  )
  // Same client-side guard the Documents tab applies (`DocumentUpload` →
  // `validateFile`). It lives here rather than in the file input so it covers
  // every caller of the upload path, and so the specific reason — wrong type,
  // too large — rides the failure toast the rest of this page already uses.
  // Without it the only feedback is a generic failure from the presign call.
  const handleUpload = useCallback(
    (itemId: string, file: File) => {
      const validation = validateFile(file)
      if (!validation.valid) {
        report(validation.error ?? 'That file cannot be uploaded')
        return Promise.resolve()
      }
      return uploadAndLinkItem(itemId, file).catch(onFailure('Could not upload the file'))
    },
    [uploadAndLinkItem, onFailure, report]
  )

  const handleCreateResponse = useCallback(
    async (item: PackItem) => {
      try {
        const artifact = await createArtifact(bidId, {
          name: item.title,
          description: item.description,
          scopeKind: 'artifact',
          artifactType: 'narrative_document',
          metadata: {
            sourceSubmissionRequirementId: item.id,
            submissionFormat: item.format ?? null,
            submissionLimit: item.limit ?? null,
            submissionMethod: item.method ?? null,
            copies: item.copies ?? null,
          },
        })
        await linkItem(item.id, {
          kind: 'artifact',
          artifactId: artifact.id,
          sectionKey: null,
        })
      } catch (error) {
        onFailure('Could not create the response')(error)
      }
    },
    [bidId, createArtifact, linkItem, onFailure]
  )

  // ===================
  // "Waiting on the team" (spec P3)
  // ===================

  /**
   * A `[TODO]` marker has no id, so acting on one first needs an overlay row.
   * The POST is idempotent on the marker's key, so this is safe on a row that
   * already has an overlay — but the id it returns is the SERVER's, which is
   * why the PATCH that follows uses the returned id and not a local guess.
   */
  const askIdFor = useCallback(
    async (entry: NeedsFromYouEntry): Promise<string | null> => {
      if (entry.kind === 'manual' || entry.kind === 'fact' || entry.kind === 'question') return entry.ask.id
      if (entry.kind === 'todo') return ensureOverlay(entry.todo, entry.overlay)
      return null
    },
    [ensureOverlay]
  )

  const handleAnswer = useCallback(
    (entry: NeedsFromYouEntry, answer: string) => {
      void (async () => {
        const askId = await askIdFor(entry)
        if (!askId) return
        await answerAsk(askId, answer)
      })().catch(onFailure('Could not save the answer'))
    },
    [askIdFor, answerAsk, onFailure]
  )

  const handleResolve = useCallback(
    (entry: NeedsFromYouEntry) => {
      void (async () => {
        const askId = await askIdFor(entry)
        if (!askId) return
        await resolveAsk(askId)
      })().catch(onFailure('Could not resolve the ask'))
    },
    [askIdFor, resolveAsk, onFailure]
  )

  const handleAskOwner = useCallback(
    (entry: NeedsFromYouEntry, ownerId: string | null) => {
      void (async () => {
        const askId = await askIdFor(entry)
        if (!askId) return
        await setAskOwner(askId, ownerId)
      })().catch(onFailure('Could not update the owner'))
    },
    [askIdFor, setAskOwner, onFailure]
  )

  const handleAddAsk = useCallback(
    (text: string, ownerId: string | null) => {
      void addAsk(text, ownerId).catch(onFailure('Could not add the ask'))
    },
    [addAsk, onFailure]
  )

  /**
   * "Apply in section" — the ReviewTab → Fix seam, reused verbatim.
   *
   * Fix hands a finding to the Ask AI agent by parking a one-shot input seed in
   * `useUnifiedAiPanelStore` and switching the panel to the `write` tab; the panel
   * consumes it on mount. That store is global, so seeding it here and then
   * navigating does exactly the same thing from another page — no new
   * mechanism, no `?ask=` round trip through the section editor.
   *
   * PROPOSE-THEN-ACCEPT is preserved by construction: this fills the composer
   * and stops. Nothing is sent, no turn runs, and the draft is untouched until
   * the user presses send and then accepts what comes back.
   */
  const handleApplyInSection = useCallback(
    (todo: BidTodo, answer: string) => {
      const panel = useUnifiedAiPanelStore.getState()
      panel.setPendingSeed(
        `Update this section using the answer below, then remove the [TODO] note.\n\n` +
          `TODO: ${todo.text}\n\nAnswer: ${answer}`
      )
      panel.setTab('write')
      void navigate(getTodoTargetPath(todo, bidId))
    },
    [bidId, navigate]
  )

  useEffect(() => {
    if (!scrollTargetId) return
    document
      .querySelector(`[data-row-id="${CSS.escape(scrollTargetId)}"]`)
      ?.scrollIntoView({ block: 'center' })
    setScrollTargetId(null)
  }, [scrollTargetId])

  useEffect(() => {
    if (!scrollToNeeds) return
    document
      .getElementById(NEEDS_FROM_YOU_ANCHOR)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    setScrollToNeeds(false)
  }, [scrollToNeeds])

  // ===================
  // Blocker chips
  // ===================

  /**
   * A chip replaces the whole filter state rather than adding to it: its number
   * is the count of rows it will show, and a leftover search or Mine toggle
   * would quietly make the list disagree with the chip that was clicked.
   */
  const applyChipFilter = useCallback(
    (next: PackFilters) => {
      setFilters(next)
      const first = pack?.items.find((item) => matchesPackFilters(item, next, currentUserId))
      if (first) setScrollTargetId(first.id)
    },
    [pack, currentUserId]
  )

  const handleShowToDo = useCallback(
    () => applyChipFilter({ ...DEFAULT_PACK_FILTERS, status: 'todo' }),
    [applyChipFilter]
  )
  const handleShowOverLimit = useCallback(
    () => applyChipFilter({ ...DEFAULT_PACK_FILTERS, overLimit: true }),
    [applyChipFilter]
  )
  const handleShowNeeds = useCallback(() => {
    setIsNeedsExpanded(true)
    setScrollToNeeds(true)
  }, [])

  /** "Review blockers" in the export dialog — the band is always on this page. */
  const scrollToBlockers = useCallback(() => {
    document.getElementById(PACK_BLOCKERS_ANCHOR)?.scrollIntoView({ block: 'center' })
  }, [])

  const renderLinkSlot = useCallback(
    (item: PackItem) => (
      <PackLinkCell
        bidId={bidId}
        item={item}
        readOnly={readOnly}
        onLinkArtifact={(artifactId, sectionKey) =>
          handleLinkArtifact(item.id, artifactId, sectionKey)
        }
        onLinkDocument={(documentId) => handleLinkDocument(item.id, documentId)}
        onUnlink={() => handleUnlink(item.id)}
        onCreateResponse={() => handleCreateResponse(item)}
        onUploadDocument={(file) => handleUpload(item.id, file)}
      />
    ),
    [
      bidId,
      readOnly,
      handleLinkArtifact,
      handleLinkDocument,
      handleUnlink,
      handleCreateResponse,
      handleUpload,
    ]
  )

  /**
   * The drawer is driven off the id, not off a captured row: the store hands
   * back a fresh pack on every mutation, so holding the object would freeze the
   * drawer on a stale copy of the item the user is editing. An id that is no
   * longer in the pack simply resolves to null and the drawer renders nothing.
   */
  const activeItem = useMemo(
    () => pack?.items.find((i) => i.id === activeItemId) ?? null,
    [pack, activeItemId]
  )
  const closeDrawer = useCallback(() => setActiveItemId(null), [])

  /**
   * `?item=<submissionRequirementId>` — the pack-level deep link, following the
   * ComplianceMatrixExtraction rules:
   *
   * - It waits for a loaded pack. Spending the link against a still-loading
   *   page would toast "no longer in the pack" about an item that simply has
   *   not arrived, and burn the link on the way past.
   * - The consumed-value ref is what stops the drawer re-opening: the effect
   *   re-runs on every `pack` identity change (a fresh object per mutation),
   *   and in the beat before the spent param leaves the URL it would re-open
   *   a drawer the user may already have closed. It resets once the param is
   *   gone, so a later link to this same page (a notification clicked while
   *   the pack is open) is honoured rather than ignored.
   * - Widen on a hidden row: if the current filters hide the target, they are
   *   cleared first — a drawer open on a row the list doesn't show reads as a
   *   bug. Scroll happens after the next commit (`scrollTargetId`), once the
   *   row exists; a done row folded away in PackList is unfolded by the same
   *   render, because PackList expands the group holding `activeItemId`.
   * - Only `item` is spent, with `{replace: true}`, so sibling params survive.
   */
  const consumedItemParamRef = useRef<string | null>(null)
  useEffect(() => {
    const itemParam = searchParams.get('item')
    if (!itemParam) {
      consumedItemParamRef.current = null
      return
    }
    if (consumedItemParamRef.current === itemParam) return
    if (!pack) return
    consumedItemParamRef.current = itemParam

    const target = pack.items.find((i) => i.id === itemParam)
    if (!target) {
      toast.info('That item is no longer in the pack')
    } else {
      if (!matchesPackFilters(target, filters, currentUserId)) {
        setFilters(DEFAULT_PACK_FILTERS)
      }
      setActiveItemId(itemParam)
      setScrollTargetId(itemParam)
    }

    // Spent on both paths — a link left in the URL re-toasts, and survives a
    // reload to re-open a drawer the user has since closed.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('item')
        return next
      },
      { replace: true }
    )
    // A filter change re-runs this, harmlessly: the ref makes a spent (or
    // absent) link a no-op.
  }, [searchParams, pack, setSearchParams, filters, currentUserId])

  const allItems = pack?.items
  const filteredItems = useMemo(
    () => filterPackItems(allItems ?? [], filters, currentUserId),
    [allItems, filters, currentUserId]
  )
  const mineCount = useMemo(
    () =>
      currentUserId === null
        ? 0
        : (allItems ?? []).filter((item) => item.ownerId === currentUserId).length,
    [allItems, currentUserId]
  )

  if (isLoading && !pack) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Loading submission pack...</p>
      </div>
    )
  }

  if (error && !pack) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="h-5 w-5 text-destructive" />
        <p className="mt-2 text-sm text-destructive">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => void load(bidId)}>
          Try Again
        </Button>
      </div>
    )
  }

  const items = pack?.items ?? []
  const removedByAddendum = pack?.removedByAddendum ?? []

  return (
    <section aria-label="Submission Pack" className="space-y-6">
      <PackHeader
        bidId={bidId}
        rollup={pack?.rollup ?? EMPTY_ROLLUP}
        blockers={pack?.blockers ?? EMPTY_BLOCKERS}
        deadline={pack?.deadline ?? null}
        deadlineHasTime={pack?.deadlineHasTime ?? null}
        items={items}
        readOnly={readOnly}
        onReviewBlockers={scrollToBlockers}
      />

      {/* Always first, never collapsed while non-empty (spec P2.2). It must NOT
          be gated on the checklist having rows: `mandatoryAtRisk` and
          `openTodos` are counted from the compliance matrix and from draft
          annotations, so a bid with zero submission requirements can still have
          real blockers. Only the green all-clear is suppressed on an empty
          pack, where it would read as a false all-clear rather than a
          reassurance. */}
      {pack && (
        <PackBlockers
          bidId={bidId}
          blockers={pack.blockers}
          removedByAddendum={removedByAddendum}
          needsCount={pack.needsFromYou.length}
          hideWhenClear={items.length === 0 && removedByAddendum.length === 0}
          onShowToDo={handleShowToDo}
          onShowOverLimit={handleShowOverLimit}
          onShowNeeds={handleShowNeeds}
        />
      )}

      {/* Between the blockers and the list (spec P2.3). NOT gated on the
          checklist having rows: todos and manual asks exist independently of
          it, and the section collapses to its own count line when empty. */}
      {pack && (
        <NeedsFromYou
          bidId={bidId}
          entries={pack.needsFromYou}
          teamMembers={teamMembers}
          readOnly={readOnly}
          isExpanded={isNeedsExpanded}
          onExpandedChange={setIsNeedsExpanded}
          onAnswer={handleAnswer}
          onResolve={handleResolve}
          onSetOwner={handleAskOwner}
          onAddAsk={handleAddAsk}
          onApplyInSection={handleApplyInSection}
        />
      )}

      {items.length === 0 && removedByAddendum.length === 0 && !showEmptyList ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ClipboardList className="h-5 w-5 text-subtle" />
          <p className="mt-2 text-sm text-muted-foreground">
            No submission requirements yet — run Analysis or add an item
          </p>
          {!readOnly && (
            <Button variant="outline" className="mt-4" onClick={() => setShowEmptyList(true)}>
              Add an item
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {items.length > 0 && (
            <PackFilterBar
              filters={filters}
              onChange={setFilters}
              rollup={pack?.rollup ?? EMPTY_ROLLUP}
              totalCount={items.length}
              shownCount={filteredItems.length}
              mineCount={mineCount}
              canFilterMine={currentUserId !== null}
            />
          )}
          <div className="flex items-start gap-3">
            {/* min-w-[280px], not min-w-0: a flex-1 column that can reach 0 lets
                the list vanish entirely once the drawer claims its 400px. */}
            <div className="min-w-[280px] flex-1">
              <PackList
                bidId={bidId}
                items={filteredItems}
                removedByAddendum={removedByAddendum}
                teamMembers={teamMembers}
                readOnly={readOnly}
                deadline={pack?.deadline ?? null}
                activeItemId={activeItemId}
                foldDone={shouldFoldDone(filters)}
                isFiltered={isPackFilterActive(filters)}
                initialAddTo={showEmptyList ? 'attachment' : null}
                onRowActivate={setActiveItemId}
                linkSlot={renderLinkSlot}
                onSetStatus={handleSetStatus}
                onSetOwner={handleSetOwner}
                onSetAcknowledged={handleSetAcknowledged}
                onAddItem={handleAddItem}
                onRemoveItem={handleRemoveItem}
              />
            </div>
            {/* Height-bounds the docked aside so its body scrolls internally
                rather than running the page. Below lg the drawer portals into a
                Sheet, so this wrapper must be `hidden` there — left visible it
                reserves 340px of empty column behind the Sheet's scrim.
                top-16, not top-4: the PackHeader is sticky at top-0/z-10 and
                ~60px tall, so anything stuck shallower slides underneath it. */}
            {activeItem && (
              <div className="sticky top-16 hidden max-h-[calc(100vh-10rem)] min-w-[340px] shrink self-start lg:flex">
                <SubmissionItemDrawer
                  item={activeItem}
                  onClose={closeDrawer}
                  readOnly={readOnly}
                  deadline={pack?.deadline ?? null}
                  teamMembers={teamMembers}
                  onOwnerChange={handleSetOwner}
                  onAcknowledgeChange={handleSetAcknowledged}
                  linkSlot={renderLinkSlot}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
